<template>
    <v-dialog v-model="dialog" max-width="1200px" scrollable persistent>
        <v-card class="activity-dialog-card">
            <v-card-title class="dialog-header">
                <div class="dialog-header-content">
                    <div>
                        <div class="dialog-title">
                            <v-icon color="white" class="mr-3">mdi-history</v-icon>
                            {{ title }}
                        </div>
                        <div class="dialog-subtitle">{{ subtitle }}</div>
                    </div>
                    <v-btn icon dark @click="closeDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
            </v-card-title>

            <v-divider></v-divider>

            <v-card-text class="pa-0" style="max-height: 600px;">
                <v-skeleton-loader type="table" v-if="loading && activities.length === 0"></v-skeleton-loader>
                <v-data-table
                    v-else
                    :headers="headers"
                    :items="activities"
                    :options.sync="options"
                    :server-items-length="totalItems"
                    :loading="loading"
                    :footer-props="{
                        'items-per-page-options': [5, 10, 25, 50]
                    }"
                    class="activity-table"
                >
                    <template v-slot:item.created_at="{ item }">
                        <div class="timestamp-cell">
                            <div class="date-text">{{ $date(item.created_at).format('MMM DD, YYYY') }}</div>
                            <div class="time-text">{{ $date(item.created_at).format('h:mm A') }}</div>
                        </div>
                    </template>

                    <template v-slot:item.user="{ item }">
                        <div class="user-cell">
                            <v-avatar size="28" color="primary" class="mr-2">
                                <span class="white--text text-caption">{{ getUserInitials(item.user?.name) }}</span>
                            </v-avatar>
                            <div>
                                <div class="user-name">{{ item.user?.name }}</div>
                                <div class="user-id">ID: {{ item.user_id }}</div>
                            </div>
                        </div>
                    </template>

                    <template v-slot:item.message="{ item }">
                        <div class="message-cell">{{ item.message }}</div>
                    </template>

                    <template v-slot:item.details="{ item }">
                        <div class="details-cell">
                            <v-chip
                                v-if="item.lead_id"
                                x-small
                                color="blue"
                                text-color="white"
                                class="ma-1"
                            >
                                <v-icon x-small left>mdi-account</v-icon>
                                Lead: {{ item.lead?.first_name }} {{ item.lead?.last_name }}
                            </v-chip>

                            <v-chip
                                v-if="item.quote_id"
                                x-small
                                color="purple"
                                text-color="white"
                                class="ma-1"
                            >
                                <v-icon x-small left>mdi-file-document</v-icon>
                                Quote: {{ item.quote?.name }}
                            </v-chip>

                            <v-chip
                                v-if="item.event_id"
                                x-small
                                color="orange"
                                text-color="white"
                                class="ma-1"
                            >
                                <v-icon x-small left>mdi-calendar</v-icon>
                                Event: {{ item.event?.name }}
                            </v-chip>

                            <v-chip
                                v-if="item.invitee_id"
                                x-small
                                color="teal"
                                text-color="white"
                                class="ma-1"
                            >
                                <v-icon x-small left>mdi-account-group</v-icon>
                                Invitee: {{ item.invitee?.name }}
                            </v-chip>

                            <v-chip
                                v-if="item.supplier_id"
                                x-small
                                color="indigo"
                                text-color="white"
                                class="ma-1"
                            >
                                <v-icon x-small left>mdi-store</v-icon>
                                Supplier: {{ item.supplier?.supplier_name }}
                            </v-chip>
                        </div>
                    </template>
                </v-data-table>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions class="pa-4">
                <v-spacer></v-spacer>
                <v-btn color="grey darken-1" text @click="closeDialog">
                    Close
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    props: {
        value: {
            type: Boolean,
            default: false
        },
        title: {
            type: String,
            default: 'Activity History'
        },
        subtitle: {
            type: String,
            default: 'View all activity records'
        },
        activities: {
            type: Array,
            default: () => []
        },
        filterType: {
            type: String,
            default: 'all', // 'all', 'lead', 'quote'
            validator: (value) => ['all', 'lead', 'quote'].includes(value)
        },
        totalItems: {
            type: Number,
            default: 0
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            headers: [
                { text: 'Timestamp', value: 'created_at', width: '150px', sortable: false },
                { text: 'User', value: 'user', width: '180px', sortable: false },
                { text: 'Description', value: 'message', sortable: false },
                { text: 'Related Items', value: 'details', sortable: false },
            ],
            options: {
                page: 1,
                itemsPerPage: 10
            },
        }
    },
    computed: {
        dialog: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            }
        }
    },
    methods: {
        getUserInitials(name) {
            if (!name) return '??'
            const parts = name.trim().split(' ')
            if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        },
        closeDialog() {
            this.$emit('input', false)
            this.$emit('close')
        }
    },
    watch: {
        value(newVal) {
            // Reset options when dialog closes
            if (!newVal) {
                this.options = {
                    page: 1,
                    itemsPerPage: 10
                }
            }
        },
        options: {
            handler(newVal, oldVal) {
                // Only emit if dialog is open and values actually changed
                if (this.value && oldVal && (newVal.page !== oldVal.page || newVal.itemsPerPage !== oldVal.itemsPerPage)) {
                    this.$emit('options-changed', this.options)
                }
            },
            deep: true
        }
    }
}
</script>

<style scoped lang="scss">
.activity-dialog-card {
    border-radius: 8px !important;
}

.dialog-header {
    background: linear-gradient(135deg, #3988AA 0%, #2a6a88 100%);
    padding: 20px 24px !important;
}

.dialog-header-content {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.dialog-title {
    font-family: Manrope, sans-serif;
    font-size: 20px;
    font-weight: 700;
    color: white;
    display: flex;
    align-items: center;
    margin-bottom: 4px;
}

.dialog-subtitle {
    font-family: Manrope, sans-serif;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.85);
    font-weight: 400;
    margin-left: 40px;
}

.activity-table {
    :deep(tbody tr) {
        &:hover {
            background-color: #f8f9fa !important;
        }
    }

    :deep(td) {
        padding: 12px !important;
        border-bottom: 1px solid #eceff1 !important;
    }

    :deep(th) {
        font-family: Manrope, sans-serif;
        font-weight: 700 !important;
        font-size: 11px !important;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: #546e7a !important;
        background-color: #fafafa !important;
    }
}

.timestamp-cell {
    font-family: Manrope, sans-serif;

    .date-text {
        font-size: 13px;
        font-weight: 600;
        color: #2c3e50;
        margin-bottom: 2px;
    }

    .time-text {
        font-size: 11px;
        color: #95a5a6;
        font-weight: 400;
    }
}

.user-cell {
    display: flex;
    align-items: center;
    font-family: Manrope, sans-serif;

    .user-name {
        font-size: 13px;
        font-weight: 600;
        color: #2c3e50;
        margin-bottom: 2px;
    }

    .user-id {
        font-size: 10px;
        color: #95a5a6;
    }
}

.message-cell {
    font-family: Manrope, sans-serif;
    font-size: 13px;
    color: #34495e;
    line-height: 1.5;
}

.details-cell {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;

    :deep(.v-chip) {
        font-family: Manrope, sans-serif;
        font-weight: 500;
    }
}
</style>
